import { Check, Monitor, Moon, Sun } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import type { ComponentType, ReactNode } from "react";
import { useDismiss } from "@/hooks/useDismiss";
import { useI18n } from "@/hooks/useI18n";
import {
    usePreferences,
    type SupportedLanguage,
    type SupportedThemeMode,
} from "@/providers/PreferencesProvider";

/* -------------------------------------------------------------------------- */
/* Shared styling (token-driven so it works in both light and dark mode)      */
/* -------------------------------------------------------------------------- */

const PILL_CLASS =
    "app-shell-surface app-shell-border app-shell-text-muted inline-flex h-9 w-9 items-center justify-center gap-2 rounded-full border shadow-sm transition-all lg:h-auto lg:w-auto lg:py-1.5 lg:pl-3 lg:pr-2.5";

const MENU_CLASS =
    "app-shell-panel app-shell-border absolute right-0 z-50 mt-2 overflow-hidden rounded-2xl border py-2 shadow-xl";

const MENU_ITEM_CLASS =
    "flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors";

function ChevronIcon({ open }: { open: boolean }) {
    return (
        <svg
            className={`app-text-soft hidden h-4 w-4 transition-transform lg:block ${
                open ? "rotate-180" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
        >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
    );
}

function CheckIcon() {
    return <Check className="h-4 w-4 text-emerald-500" />;
}

/* -------------------------------------------------------------------------- */
/* Help Center                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Renders nothing unless `VITE_HELP_CENTER_URL` is configured — same behaviour
 * as the Portal, so the button never points at a dead link.
 */
export function HelpCenterButton() {
    const { t } = useI18n();
    const { language, effectiveThemeMode } = usePreferences();

    const url = useMemo(() => {
        const baseUrl = (import.meta.env.VITE_HELP_CENTER_URL as string | undefined)?.trim();
        if (!baseUrl) return null;

        const params = new URLSearchParams();
        params.set("theme", effectiveThemeMode);
        params.set("lang", language);
        const separator = baseUrl.includes("?") ? "&" : "?";
        return `${baseUrl}${separator}${params.toString()}`;
    }, [language, effectiveThemeMode]);

    if (!url) return null;

    return (
        <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="app-shell-surface app-shell-border app-shell-text-muted app-shell-hover hidden items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold shadow-sm transition-all lg:inline-flex"
        >
            <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
            >
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.75}
                    d="M12 21a9 9 0 100-18 9 9 0 000 18z"
                />
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.75}
                    d="M9.75 9.75a2.25 2.25 0 114.5 0c0 1.5-2.25 2-2.25 3.75M12 17.25h.008v.008H12v-.008z"
                />
            </svg>
            {t("header.helpCenter")}
        </a>
    );
}

/* -------------------------------------------------------------------------- */
/* Theme                                                                      */
/* -------------------------------------------------------------------------- */

const THEME_OPTIONS: { value: SupportedThemeMode; icon: ComponentType<{ size?: number }> }[] = [
    { value: "system", icon: Monitor },
    { value: "light", icon: Sun },
    { value: "dark", icon: Moon },
];

export function ThemeMenu() {
    const { t } = useI18n();
    const { themeMode, effectiveThemeMode, setThemeMode } = usePreferences();
    const [open, setOpen] = useState(false);
    const close = useCallback(() => setOpen(false), []);
    const ref = useDismiss(open, close);

    const CurrentIcon = themeMode === "system" ? Monitor : effectiveThemeMode === "dark" ? Moon : Sun;

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={() => setOpen((prev) => !prev)}
                aria-label={`${t("theme.label")}: ${themeMode}`}
                aria-haspopup="menu"
                aria-expanded={open}
                className={PILL_CLASS}
            >
                <CurrentIcon size={16} />
                <span className="app-text-primary hidden text-sm font-semibold capitalize tracking-wide lg:inline">
                    {t(`theme.${themeMode}`)}
                </span>
                <ChevronIcon open={open} />
            </button>

            {open && (
                <div role="menu" className={`${MENU_CLASS} w-48`}>
                    {THEME_OPTIONS.map(({ value, icon: Icon }) => {
                        const selected = themeMode === value;
                        return (
                            <button
                                key={value}
                                type="button"
                                role="menuitem"
                                onClick={() => {
                                    setThemeMode(value);
                                    setOpen(false);
                                }}
                                className={`${MENU_ITEM_CLASS} ${
                                    selected
                                        ? "app-content-subtle app-text-primary font-semibold"
                                        : "app-shell-text-muted app-shell-hover"
                                }`}
                            >
                                <span className="app-content-subtle app-shell-text-muted flex h-6 w-6 shrink-0 items-center justify-center rounded-full">
                                    <Icon size={16} />
                                </span>
                                <span className="flex-1">{t(`theme.${value}`)}</span>
                                {selected && <CheckIcon />}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/* Language                                                                   */
/* -------------------------------------------------------------------------- */

function Flag({ code }: { code: SupportedLanguage }): ReactNode {
    if (code === "id") {
        return (
            <svg viewBox="0 0 36 36" className="h-full w-full" aria-hidden="true">
                <rect width="36" height="18" fill="#FF0000" />
                <rect y="18" width="36" height="18" fill="#FFFFFF" />
            </svg>
        );
    }

    return (
        <svg viewBox="0 0 36 36" className="h-full w-full" aria-hidden="true">
            <rect width="36" height="36" fill="#00247D" />
            <path d="M0 0 L36 36 M36 0 L0 36" stroke="#FFFFFF" strokeWidth="6" />
            <path d="M0 0 L36 36 M36 0 L0 36" stroke="#CF142B" strokeWidth="4" />
            <path d="M18 0 V36 M0 18 H36" stroke="#FFFFFF" strokeWidth="6" />
            <path d="M18 0 V36 M0 18 H36" stroke="#CF142B" strokeWidth="4" />
        </svg>
    );
}

const LANGUAGE_OPTIONS: SupportedLanguage[] = ["id", "en"];

export function LanguageMenu() {
    const { t } = useI18n();
    const { language, setLanguage } = usePreferences();
    const [open, setOpen] = useState(false);
    const close = useCallback(() => setOpen(false), []);
    const ref = useDismiss(open, close);

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={() => setOpen((prev) => !prev)}
                aria-label={`${t("language.label")}: ${language}`}
                aria-haspopup="menu"
                aria-expanded={open}
                className={PILL_CLASS}
            >
                <span className="h-4 w-4 shrink-0 overflow-hidden rounded-full shadow-sm">
                    <Flag code={language} />
                </span>
                <span className="app-text-primary hidden text-sm font-semibold uppercase tracking-wide lg:inline">
                    {language}
                </span>
                <ChevronIcon open={open} />
            </button>

            {open && (
                <div role="menu" className={`${MENU_CLASS} w-44`}>
                    {LANGUAGE_OPTIONS.map((value) => {
                        const selected = language === value;
                        return (
                            <button
                                key={value}
                                type="button"
                                role="menuitem"
                                lang={value}
                                onClick={() => {
                                    setLanguage(value);
                                    setOpen(false);
                                }}
                                className={`${MENU_ITEM_CLASS} ${
                                    selected
                                        ? "app-content-subtle app-text-primary font-semibold"
                                        : "app-shell-text-muted app-shell-hover"
                                }`}
                            >
                                <span className="h-6 w-6 shrink-0 overflow-hidden rounded-full shadow-sm">
                                    <Flag code={value} />
                                </span>
                                <span className="flex-1">{t(`language.${value}`)}</span>
                                {selected && <CheckIcon />}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
